import { useState, useRef } from "react";

interface Props {
  prompt: string;
  onSubmit: () => void;
}

export default function SubmitStep({ prompt, onSubmit }: Props) {
  const [uploadedImage, setUploadedImage] = useState<string | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [noteText, setNoteText] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) setUploadedImage(URL.createObjectURL(file));
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0];
    if (file) setUploadedImage(URL.createObjectURL(file));
  };

  const handleSubmit = () => {
    if (!uploadedImage && !noteText.trim()) return;
    setSubmitting(true);
    setTimeout(() => {
      setSubmitting(false);
      onSubmit();
    }, 2000);
  };

  const canSubmit = !!(uploadedImage || noteText.trim());

  return (
    <div className="bg-white rounded-2xl p-8 mb-6">
      <div className="flex items-center gap-2 mb-2">
        <div className="w-5 h-5 flex items-center justify-center">
          <i className="ri-upload-cloud-2-line text-[#00D873] text-lg" />
        </div>
        <span className="text-xs font-bold text-[#00D873] tracking-widest uppercase">
          결과물 제출
        </span>
      </div>
      <h2 className="text-2xl font-extrabold text-[#191F28] mb-1">
        직접 만든 결과물을 공유해 보세요!
      </h2>
      <p className="text-[#8B95A1] text-sm mb-7 leading-relaxed">
        아래 프롬프트로 AI 이미지를 생성한 후 결과물을 첨부해주세요.
      </p>

      {/* Prompt Card */}
      <div className="bg-[#191F28] rounded-2xl p-5 mb-7">
        <p className="text-xs text-[#8B95A1] mb-2.5 flex items-center gap-1.5 font-semibold">
          <span className="w-4 h-4 flex items-center justify-center">
            <i className="ri-terminal-box-line" />
          </span>
          완성된 프롬프트
        </p>
        <code className="text-[#00D873] text-sm font-mono leading-relaxed break-words">
          {prompt || "프롬프트 빌더에서 입력한 프롬프트가 여기에 표시됩니다"}
        </code>
      </div>

      {/* Image Upload */}
      <div className="mb-5">
        <label className="block text-sm font-bold text-[#191F28] mb-2.5">
          생성된 이미지 첨부
        </label>
        <div
          onDragOver={(e) => { e.preventDefault(); setIsDragging(true); }}
          onDragLeave={() => setIsDragging(false)}
          onDrop={handleDrop}
          onClick={() => fileInputRef.current?.click()}
          className={`relative rounded-2xl border-2 border-dashed transition-all duration-200 cursor-pointer flex items-center justify-center overflow-hidden ${
            isDragging
              ? "border-[#00D873] bg-[#E8F5E9]"
              : uploadedImage
              ? "border-[#00D873]/30"
              : "border-[#E0E2E6] bg-[#F2F4F6] hover:border-[#00D873]/50 hover:bg-[#F8FFF9]"
          }`}
          style={{ minHeight: "160px" }}
        >
          {uploadedImage ? (
            <div className="relative w-full">
              <img
                src={uploadedImage}
                alt="업로드된 이미지"
                className="w-full h-52 object-cover object-top rounded-2xl"
              />
              <div className="absolute inset-0 bg-black/0 hover:bg-black/20 transition-colors rounded-2xl flex items-center justify-center group">
                <span className="opacity-0 group-hover:opacity-100 text-white text-xs font-bold bg-black/60 px-3 py-1.5 rounded-full transition-opacity">
                  이미지 변경
                </span>
              </div>
            </div>
          ) : (
            <div className="flex flex-col items-center gap-3 py-10">
              <div className="w-12 h-12 flex items-center justify-center bg-white rounded-2xl">
                <i className="ri-image-add-line text-2xl text-[#8B95A1]" />
              </div>
              <div className="text-center">
                <p className="text-sm font-semibold text-[#191F28]">
                  AI로 생성한 이미지를 올려주세요
                </p>
                <p className="text-xs text-[#8B95A1] mt-1">PNG, JPG, WEBP · 최대 10MB</p>
              </div>
            </div>
          )}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      </div>

      {/* Note */}
      <div className="mb-8">
        <label className="block text-sm font-bold text-[#191F28] mb-2.5">
          한 줄 소감 <span className="text-[#8B95A1] font-normal">(선택)</span>
        </label>
        <textarea
          value={noteText}
          onChange={(e) => setNoteText(e.target.value.slice(0, 200))}
          placeholder="이번 챌린지를 통해 배운 점이나 느낀 점을 공유해주세요!"
          rows={2}
          className="w-full bg-[#F2F4F6] rounded-2xl p-4 text-sm text-[#191F28] placeholder-[#8B95A1] outline-none focus:ring-2 focus:ring-[#00D873]/30 resize-none transition-all"
        />
        <p className="text-xs text-[#8B95A1] mt-1.5 text-right">{noteText.length}/200</p>
      </div>

      <button
        onClick={handleSubmit}
        disabled={!canSubmit || submitting}
        className={`w-full py-5 rounded-2xl text-[17px] font-extrabold transition-all duration-200 whitespace-nowrap ${
          canSubmit && !submitting
            ? "bg-[#00D873] text-white cursor-pointer hover:opacity-90 hover:-translate-y-0.5"
            : "bg-[#E0E2E6] text-[#8B95A1] cursor-not-allowed"
        }`}
        style={canSubmit && !submitting ? { boxShadow: "0 6px 24px rgba(0,216,115,0.35)" } : {}}
      >
        {submitting ? (
          <span className="flex items-center justify-center gap-3">
            <span className="w-5 h-5 flex items-center justify-center">
              <i className="ri-loader-4-line animate-spin text-xl" />
            </span>
            AI 분석 중...
          </span>
        ) : (
          "결과물 제출하고 피드에 자랑하기 🚀"
        )}
      </button>
    </div>
  );
}
